import { useState, useEffect } from 'react';
import { analyticsService, AnalyticsPeriod, DashboardMetrics, TrendDataPoint, KPIData } from '@services/analyticsService';

export function useDashboardMetrics(period: AnalyticsPeriod = 'today', startDate?: string, endDate?: string) {
  const [data, setData] = useState<DashboardMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadMetrics = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const metrics = await analyticsService.getDashboardMetrics(period, startDate, endDate);
        if (isMounted) {
          setData(metrics);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.response?.data?.error?.message || 'Metrikler yüklenemedi');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadMetrics();

    const interval = setInterval(loadMetrics, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [period, startDate, endDate]);

  return { data, isLoading, error };
}

export function useTrends(period: AnalyticsPeriod = 'today', startDate?: string, endDate?: string) {
  const [data, setData] = useState<TrendDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadTrends = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const trends = await analyticsService.getTrends(period, startDate, endDate);
        if (isMounted) {
          setData(trends);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.response?.data?.error?.message || 'Trend verileri yüklenemedi');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadTrends();

    return () => {
      isMounted = false;
    };
  }, [period, startDate, endDate]);

  return { data, isLoading, error };
}

export function useKPIs() {
  const [data, setData] = useState<KPIData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadKPIs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const kpis = await analyticsService.getKPIs();
        if (isMounted) {
          setData(kpis);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.response?.data?.error?.message || 'KPI verileri yüklenemedi');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadKPIs();

    const interval = setInterval(loadKPIs, 60000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  return { data, isLoading, error };
}
